import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Popover,
  Slider,
  Typography,
  Fade,
} from '@mui/material';
import {
  Create as PenIcon,
  TextFields as TextIcon,
  FullscreenExit as FullscreenExitIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Add as AddPageIcon,
  SwapVert as MoveToolbarIcon,
} from '@mui/icons-material';
import { NotebookCanvas } from './NotebookCanvas';
import type { NotebookCanvasRef, ToolType } from './NotebookCanvas';
import type { PageTemplate as PageTemplateType, PaperSize } from '@/types/notebook';
import { PAPER_PIXELS } from '@/types/notebook';

type ToolbarPosition = 'top' | 'bottom';

type FullscreenNotebookProps = {
  canvasRef: React.RefObject<NotebookCanvasRef | null>;
  template: PageTemplateType;
  paperSize: PaperSize;
  initialData?: string;
  readOnly?: boolean;
  tool: ToolType;
  onToolChange: (tool: ToolType) => void;
  penColor: string;
  onColorChange: (color: string) => void;
  penWidth: number;
  onWidthChange: (width: number) => void;
  onCanvasChange?: (data: string) => void;
  onExit: () => void;
  // Page navigation
  currentPageIndex: number;
  pageCount: number;
  onPageChange: (index: number) => void;
  onAddPage?: () => void;
  canEdit: boolean;
};

const PRESET_COLORS = [
  '#000000', // Black
  '#333333', // Dark Gray
  '#0f2ccf', // Primary Blue
  '#5a48f5', // Purple
  '#df3336', // Red
  '#4b916d', // Green
  '#ffa500', // Orange
];

// Minimal padding for maximum canvas area
const TOOLBAR_MARGIN = 12;

export const FullscreenNotebook = ({
  canvasRef,
  template,
  paperSize,
  initialData,
  readOnly = false,
  tool,
  onToolChange,
  penColor,
  onColorChange,
  penWidth,
  onWidthChange,
  onCanvasChange,
  onExit,
  currentPageIndex,
  pageCount,
  onPageChange,
  onAddPage,
  canEdit,
}: FullscreenNotebookProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [toolbarPosition, setToolbarPosition] = useState<ToolbarPosition>('bottom');
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const [scale, setScale] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimer = useRef<NodeJS.Timeout | null>(null);

  const dimensions = PAPER_PIXELS[paperSize];

  // Calculate scale to fit canvas in viewport - maximize canvas area
  const calculateScale = useCallback(() => {
    if (!containerRef.current) return 1;

    const containerRect = containerRef.current.getBoundingClientRect();
    // Use almost full viewport, only leave small margin for toolbar overlay
    const availableHeight = containerRect.height - TOOLBAR_MARGIN * 2;
    const availableWidth = containerRect.width - TOOLBAR_MARGIN * 2;

    const scaleX = availableWidth / dimensions.width;
    const scaleY = availableHeight / dimensions.height;

    // Use the smaller scale to ensure it fits completely
    return Math.min(scaleX, scaleY, 1); // Don't scale up beyond 100%
  }, [dimensions.width, dimensions.height]);

  // Update scale on mount and resize
  useEffect(() => {
    const updateScale = () => {
      setScale(calculateScale());
    };

    updateScale();

    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', updateScale);

    return () => {
      window.removeEventListener('resize', updateScale);
      window.removeEventListener('orientationchange', updateScale);
    };
  }, [calculateScale]);

  // Auto-hide controls after inactivity
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimer.current) {
      clearTimeout(hideControlsTimer.current);
    }
    hideControlsTimer.current = setTimeout(() => {
      if (tool === 'pen' || tool === 'highlighter') {
        setShowControls(false);
      }
    }, 3000);
  }, [tool]);

  useEffect(() => {
    resetHideTimer();
    return () => {
      if (hideControlsTimer.current) {
        clearTimeout(hideControlsTimer.current);
      }
    };
  }, [resetHideTimer]);

  const handleInteraction = () => {
    resetHideTimer();
  };

  // Page navigation
  const handlePrevPage = () => {
    if (currentPageIndex > 0) {
      onPageChange(currentPageIndex - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPageIndex < pageCount - 1) {
      onPageChange(currentPageIndex + 1);
    }
  };

  // Swipe gesture handling
  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    const threshold = 100;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentPageIndex < pageCount - 1) {
        handleNextPage();
      } else if (diff < 0 && currentPageIndex > 0) {
        handlePrevPage();
      }
    }

    touchStartX.current = null;
  };

  const toggleToolbarPosition = () => {
    setToolbarPosition((prev) => (prev === 'top' ? 'bottom' : 'top'));
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onExit();
        return;
      }

      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        handlePrevPage();
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        handleNextPage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onExit, currentPageIndex, pageCount]);

  // Compact floating toolbar
  const CompactToolbar = (
    <Fade in={showControls}>
      <Box
        sx={{
          position: 'absolute',
          [toolbarPosition]: TOOLBAR_MARGIN,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          px: 1,
          py: 0.5,
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(8px)',
          borderRadius: 2,
          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
          zIndex: 10,
        }}
        data-testid="fullscreen-toolbar"
      >
        {/* Exit fullscreen */}
        <Tooltip title="Exit (Esc)">
          <IconButton onClick={onExit} size="small" data-testid="exit-fullscreen-btn">
            <FullscreenExitIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box sx={{ mx: 0.25, height: 20, borderLeft: 1, borderColor: 'divider' }} />

        {/* Drawing tools */}
        {!readOnly && (
          <>
            <Tooltip title="Draw">
              <IconButton
                onClick={() => onToolChange('pen')}
                size="small"
                color={tool === 'pen' ? 'primary' : 'default'}
                data-testid="tool-pen"
              >
                <PenIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Text">
              <IconButton
                onClick={() => onToolChange('text')}
                size="small"
                color={tool === 'text' ? 'primary' : 'default'}
                data-testid="tool-text"
              >
                <TextIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Color Picker */}
            <Tooltip title="Color">
              <IconButton
                onClick={(e) => setColorAnchor(e.currentTarget)}
                size="small"
                sx={{
                  backgroundColor: penColor,
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  border: '2px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: penColor,
                    opacity: 0.8,
                  },
                }}
                data-testid="color-picker-btn"
              />
            </Tooltip>

            <Box sx={{ mx: 0.25, height: 20, borderLeft: 1, borderColor: 'divider' }} />
          </>
        )}

        {/* Page navigation */}
        <Tooltip title="Previous page">
          <span>
            <IconButton
              onClick={handlePrevPage}
              disabled={currentPageIndex === 0}
              size="small"
              data-testid="prev-page-btn"
            >
              <PrevIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Typography
          variant="caption"
          sx={{
            minWidth: 36,
            textAlign: 'center',
            userSelect: 'none',
            fontWeight: 500,
            color: 'text.secondary',
          }}
          data-testid="page-indicator"
        >
          {currentPageIndex + 1}/{pageCount}
        </Typography>

        <Tooltip title="Next page">
          <span>
            <IconButton
              onClick={handleNextPage}
              disabled={currentPageIndex === pageCount - 1}
              size="small"
              data-testid="next-page-btn"
            >
              <NextIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Add page */}
        {canEdit && onAddPage && (
          <Tooltip title="New page">
            <IconButton onClick={onAddPage} size="small" data-testid="add-page-btn">
              <AddPageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ mx: 0.25, height: 20, borderLeft: 1, borderColor: 'divider' }} />

        {/* Move toolbar */}
        <Tooltip title={toolbarPosition === 'top' ? 'Move to bottom' : 'Move to top'}>
          <IconButton onClick={toggleToolbarPosition} size="small" data-testid="move-toolbar-btn">
            <MoveToolbarIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Fade>
  );

  // Color picker popover
  const ColorPickerPopover = (
    <Popover
      open={Boolean(colorAnchor)}
      anchorEl={colorAnchor}
      onClose={() => setColorAnchor(null)}
      anchorOrigin={{ vertical: toolbarPosition === 'top' ? 'bottom' : 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: toolbarPosition === 'top' ? 'top' : 'bottom', horizontal: 'center' }}
      sx={{ zIndex: 10001 }}
    >
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
          {PRESET_COLORS.map((color) => (
            <IconButton
              key={color}
              onClick={() => {
                onColorChange(color);
                setColorAnchor(null);
              }}
              sx={{
                width: 28,
                height: 28,
                backgroundColor: color,
                border: color === penColor ? '3px solid' : '1px solid',
                borderColor: color === penColor ? 'primary.main' : 'divider',
                '&:hover': {
                  backgroundColor: color,
                  opacity: 0.8,
                },
              }}
              data-testid={`color-${color.replace('#', '')}`}
            />
          ))}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Size: {penWidth}px
          </Typography>
          <Slider
            value={penWidth}
            onChange={(_, value) => onWidthChange(value as number)}
            min={1}
            max={10}
            step={1}
            size="small"
            sx={{ width: 180 }}
            data-testid="pen-size-slider"
          />
        </Box>
      </Box>
    </Popover>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: '#e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        touchAction: 'none',
      }}
      onMouseMove={handleInteraction}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleInteraction}
      data-testid="fullscreen-notebook"
    >
      {/* Canvas Container - centered and scaled to maximize area */}
      <Box
        sx={{
          transform: `scale(${scale})`,
          transformOrigin: 'center',
          transition: 'transform 0.2s ease-out',
          boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
        }}
        data-testid="canvas-container"
      >
        <NotebookCanvas
          ref={canvasRef}
          template={template}
          paperSize={paperSize}
          initialData={initialData}
          readOnly={readOnly}
          tool={tool}
          penColor={penColor}
          penWidth={penWidth}
          onCanvasChange={onCanvasChange}
        />
      </Box>

      {/* Compact Floating Toolbar */}
      {CompactToolbar}

      {/* Color Picker Popover */}
      {ColorPickerPopover}
    </Box>
  );
};
